import React, { useEffect, useState, useRef } from 'react';
import api from '../api/api';
import {
    formatDateTime,
    formatMoney,
    formatTime,
    getOrderStatusLabel,
    getPaymentMethodLabel,
} from '../utils/dashboardTranslations';

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    
    // Get base URL from environment or default to production
    const imageBase = process.env.REACT_APP_IMAGE_BASE || 'https://waselapp.group';
    
    // If it's a relative path, add the base domain
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${imageBase}${cleanPath}`;
};

function AdminOrdersManagement() {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acceptingOrders, setAcceptingOrders] = useState({});
    const [deletingOrders, setDeletingOrders] = useState({});
    const [deletingOrder, setDeletingOrder] = useState(null);
    const [deletingAllOrders, setDeletingAllOrders] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [bulkAccepting, setBulkAccepting] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [selectedOrderProducts, setSelectedOrderProducts] = useState([]);
    const [selectedOrderInfo, setSelectedOrderInfo] = useState(null);
    const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
    const [newOrdersCount, setNewOrdersCount] = useState(0);
    const intervalRef = useRef(null);
    const previousOrderCountRef = useRef(0);

    const readValue = (source, ...keys) => {
        for (const key of keys) {
            const value = source?.[key];
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    };

    const readNumberValue = (source, ...keys) => {
        const value = readValue(source, ...keys);
        return Number(value ?? 0);
    };

    const quantityBadgeStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '52px',
        padding: '0.55rem 0.95rem',
        borderRadius: '999px',
        backgroundColor: '#0d6efd',
        color: '#fff',
        fontSize: '1rem',
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: '0.03em',
        boxShadow: '0 10px 20px rgba(13, 110, 253, 0.25)'
    };

    useEffect(() => {
        fetchAllOrders();
        
        // Set up polling for live updates every 5 seconds
        intervalRef.current = setInterval(() => {
            console.log('🔄 Admin Dashboard: Background refresh for new orders...');
            fetchAllOrders(true); // true for background fetch
        }, 5000);

        // Cleanup interval on component unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        filterOrders();
    }, [orders, activeFilter]);

    const fetchAllOrders = async (isBackground = false) => {
        if (!isBackground) {
            setLoading(true);
        }
        
        try {
            const res = await api.get('/Order/admin/all');
            console.log('Admin API Response:', res.data);
            
            // Debug delivery driver information
            const ordersWithDrivers = res.data?.filter(order => order.deliveryDriver) || [];
            console.log(`Orders with drivers: ${ordersWithDrivers.length}`);
            ordersWithDrivers.forEach(order => {
                console.log(`Order ${order.id}: Driver ID=${order.deliveryDriverId}, Driver=`, order.deliveryDriver);
                if (order.deliveryDriver) {
                    console.log(`  Driver Name: '${order.deliveryDriver.name}'`);
                    console.log(`  Driver Phone: '${order.deliveryDriver.phone}'`);
                }
            });
            
            if (res.data && res.data.length > 0) {
                console.log('Sample Order:', res.data[0]);
                if (res.data[0].user) {
                    console.log('Sample User:', res.data[0].user);
                }
                if (res.data[0].orderDetails && res.data[0].orderDetails.length > 0) {
                    console.log('Sample OrderDetail:', res.data[0].orderDetails[0]);
                }
            }
            
            const newOrders = res.data || [];
            
            // Check for new orders (only for background fetch)
            if (isBackground && orders.length > 0) {
                const currentCount = newOrders.length;
                const previousCount = previousOrderCountRef.current;
                
                if (currentCount > previousCount) {
                    const newOrdersDetected = currentCount - previousCount;
                    setNewOrdersCount(prev => prev + newOrdersDetected);
                    console.log(`🔔 Admin: ${newOrdersDetected} new order(s) detected! Total new: ${newOrdersCount + newOrdersDetected}`);
                    
                    // Show browser notification for new orders
                    if (Notification.permission === 'granted') {
                        new Notification('🛒 طلب جديد!', {
                            body: `${newOrdersDetected} طلب جديد وصل إلى النظام`,
                            icon: '/favicon.ico'
                        });
                    } else if (Notification.permission !== 'denied') {
                        Notification.requestPermission().then(permission => {
                            if (permission === 'granted') {
                                new Notification('🛒 طلب جديد!', {
                                    body: `${newOrdersDetected} طلب جديد وصل إلى النظام`,
                                    icon: '/favicon.ico'
                                });
                            }
                        });
                    }
                }
            }
            
            previousOrderCountRef.current = newOrders.length;
            setOrders(newOrders);
            setLastUpdateTime(Date.now());
            
        } catch (error) {
            console.error('Error fetching all orders:', error);
            if (!isBackground) {
                alert('فشل تحميل جميع الطلبات');
            }
        } finally {
            if (!isBackground) {
                setLoading(false);
            }
        }
    };

    const filterOrders = () => {
        let filtered = [];
        switch (activeFilter) {
            case 'all':
                filtered = orders;
                break;
            case 'pending':
                filtered = orders.filter(order => order.status === 'Pending');
                break;
            case 'accepted':
                filtered = orders.filter(order => order.status === 'Accepted');
                break;
            case 'delivered':
                filtered = orders.filter(order => order.status === 'Delivered');
                break;
            default:
                filtered = orders;
        }
        setFilteredOrders(filtered);
        setSelectedOrders([]); // Clear selections when filter changes
    };

    const handleAcceptOrder = async (orderId) => {
        setAcceptingOrders(prev => ({ ...prev, [orderId]: true }));
        try {
            const estimatedDeliveryTime = new Date();
            estimatedDeliveryTime.setHours(estimatedDeliveryTime.getHours() + 1);
            
            // Send the ISO string as quoted JSON string
            const response = await api.put(`/Order/accept/${orderId}`, 
                `"${estimatedDeliveryTime.toISOString()}"`, 
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (response.status === 200) {
                alert('تم قبول الطلب بنجاح من قبل الإدارة');
                fetchAllOrders(); // This will trigger filterOrders through useEffect
            }
        } catch (error) {
            console.error('Error accepting order:', error);
            alert('فشل في قبول الطلب');
        } finally {
            setAcceptingOrders(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleBulkAccept = async () => {
        if (selectedOrders.length === 0) {
            alert('يرجى اختيار طلبات للقبول');
            return;
        }

        setBulkAccepting(true);
        try {
            const response = await api.put('/Order/admin/bulk-accept', selectedOrders);
            
            if (response.status === 200) {
                alert(`تم قبول ${selectedOrders.length} طلب بنجاح`);
                setSelectedOrders([]);
                fetchAllOrders(); // This will trigger filterOrders through useEffect
            }
        } catch (error) {
            console.error('Error bulk accepting orders:', error);
            alert('فشل في القبول المجمع للطلبات');
        } finally {
            setBulkAccepting(false);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.')) {
            return;
        }

        setDeletingOrders(prev => ({ ...prev, [orderId]: true }));
        try {
            const response = await api.delete(`/Order/delete/${orderId}`);
            
            if (response.status === 200) {
                alert('تم حذف الطلب بنجاح');
                fetchAllOrders(); // Refresh the orders list
            }
        } catch (error) {
            console.error('Error deleting order:', error);
            alert('فشل في حذف الطلب');
        } finally {
            setDeletingOrders(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleDeleteAllOrders = async () => {
        if (!window.confirm('⚠️ تحذير: هل أنت متأكد من حذف جميع الطلبات؟\n\nهذا الإجراء سيحذف جميع الطلبات نهائياً ولا يمكن التراجع عنه!\n\nاكتب "احذف الكل" في الحقل التالي للتأكيد:')) {
            return;
        }

        const confirmation = prompt('للتأكيد، اكتب "احذف الكل":');
        if (confirmation !== 'احذف الكل') {
            alert('تم إلغاء العملية');
            return;
        }

        setDeletingAllOrders(true);
        try {
            const response = await api.delete('/Order/delete-all');
            
            if (response.status === 200) {
                alert(`تم حذف جميع الطلبات بنجاح!\nالطلبات المحذوفة: ${response.data.deletedOrders}\nتفاصيل الطلبات المحذوفة: ${response.data.deletedOrderDetails}`);
                fetchAllOrders(); // Refresh the orders list
                setSelectedOrders([]); // Clear selections
            }
        } catch (error) {
            console.error('Error deleting all orders:', error);
            alert('فشل في حذف جميع الطلبات');
        } finally {
            setDeletingAllOrders(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedOrders.length === 0) {
            alert('يرجى اختيار طلبات للحذف');
            return;
        }

        if (!window.confirm(`هل أنت متأكد من حذف ${selectedOrders.length} طلب؟ لا يمكن التراجع عن هذا الإجراء.`)) {
            return;
        }

        try {
            // Delete selected orders one by one
            for (const orderId of selectedOrders) {
                await api.delete(`/Order/delete/${orderId}`);
            }
            
            alert(`تم حذف ${selectedOrders.length} طلب بنجاح`);
            setSelectedOrders([]);
            fetchAllOrders(); // Refresh the orders list
        } catch (error) {
            console.error('Error bulk deleting orders:', error);
            alert('فشل في حذف بعض الطلبات');
        }
    };

    const handleSelectOrder = (orderId, isChecked) => {
        if (isChecked) {
            setSelectedOrders(prev => [...prev, orderId]);
        } else {
            setSelectedOrders(prev => prev.filter(id => id !== orderId));
        }
    };

    const handleSelectAll = (isChecked) => {
        if (isChecked) {
            // Only select pending orders for bulk acceptance
            const pendingOrdersInFilter = filteredOrders.filter(order => order.status === 'Pending');
            setSelectedOrders(pendingOrdersInFilter.map(order => order.id));
        } else {
            setSelectedOrders([]);
        }
    };

    const handleShowProducts = (order) => {
        console.log('Order data for modal:', order);
        console.log('DeliveryFee value:', order.DeliveryFee);
        console.log('deliveryFee value:', order.deliveryFee);

        const originalTotalAmount = readNumberValue(order, 'originalTotalAmount', 'OriginalTotalAmount', 'originalTotal', 'OriginalTotal');
        const productDiscount = readNumberValue(order, 'productDiscount', 'ProductDiscount');
        const couponDiscount = readNumberValue(order, 'couponDiscount', 'CouponDiscount');
        const totalAmount = readNumberValue(order, 'totalAmount', 'TotalAmount');
        const deliveryFee = readNumberValue(order, 'deliveryFee', 'DeliveryFee');
        const pointsUsed = readNumberValue(order, 'pointsUsed', 'PointsUsed');
        const pointsEarned = readNumberValue(order, 'pointsEarned', 'PointsEarned');
        const amountPaidWithPoints = readNumberValue(order, 'amountPaidWithPoints', 'AmountPaidWithPoints');
        const amountPaidWithMoney = readNumberValue(order, 'amountPaidWithMoney', 'AmountPaidWithMoney');
        const subtotalAfterProductDiscount = readNumberValue(order, 'subtotalAfterProductDiscount', 'SubtotalAfterProductDiscount') || Math.max(0, originalTotalAmount - productDiscount);
        const amountAfterDiscount = readNumberValue(order, 'amountAfterDiscount', 'AmountAfterDiscount') || totalAmount;
        const finalAmountPaid = readNumberValue(order, 'finalAmountPaid', 'FinalAmountPaid') || amountPaidWithMoney;
        const grandTotal = readNumberValue(order, 'grandTotal', 'GrandTotal') || finalAmountPaid;
        
        setSelectedOrderProducts(order.orderDetails || order.OrderDetails || []);
        setSelectedOrderInfo({
            id: order.id,
            customerName: order.user ? `${order.user.firstName || order.user.FirstName} ${order.user.lastName || order.user.LastName}` : 'غير محدد',
            vendorName: order.vendor ? (order.vendor.name || order.vendor.Name) : 'غير محدد',
            notes: order.notes || order.Notes || '',
            total: calculateOrderTotal(order.orderDetails || order.OrderDetails || []),
            // Enhanced order information
            totalAmount,
            deliveryFee,
            pointsUsed,
            pointsEarned,
            amountPaidWithPoints,
            amountPaidWithMoney,
            amountAfterDiscount,
            originalTotal: originalTotalAmount,
            productDiscount,
            subtotalAfterProductDiscount,
            couponDiscount,
            couponCode: readValue(order, 'couponCode', 'CouponCode') || '',
            subTotal: readNumberValue(order, 'subTotal', 'SubTotal') || totalAmount,
            grandTotal,
            finalAmountPaid,
            paymentMethod: order.paymentMethod || order.PaymentMethod || 'غير محدد'
        });
        setShowProductsModal(true);
    };

    const calculateOrderTotal = (orderDetails) => {
        if (!orderDetails || orderDetails.length === 0) return 0;
        return orderDetails.reduce((total, item) => {
            const price = item.price || item.Price || 0;
            const quantity = item.quantity || item.Quantity || 0;
            return total + (price * quantity);
        }, 0);
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4>إدارة الطلبات - لوحة الإدارة</h4>
                    {/* Last update indicator */}
                    <small className="text-muted d-flex align-items-center">
                        <i className="fas fa-clock me-1"></i>
                        آخر تحديث: {formatTime(lastUpdateTime)} (بتوقيت سوريا)
                    </small>
                </div>
                <div>
                    <button 
                        className="btn btn-outline-primary me-2" 
                        onClick={() => {
                            setNewOrdersCount(0);
                            fetchAllOrders();
                        }}
                    >
                        <i className="fas fa-refresh me-1"></i>
                        تحديث
                        {newOrdersCount > 0 && (
                            <span className="badge bg-danger ms-2">{newOrdersCount}</span>
                        )}
                    </button>
                    
                    {/* Delete All Orders Button */}
                    <button 
                        className="btn btn-outline-danger me-2"
                        onClick={handleDeleteAllOrders}
                        disabled={deletingAllOrders || orders.length === 0}
                    >
                        {deletingAllOrders ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                جاري حذف جميع الطلبات...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-trash-alt me-1"></i>
                                حذف جميع الطلبات ({orders.length})
                            </>
                        )}
                    </button>

                    {selectedOrders.length > 0 && (
                        <>
                            <button 
                                className="btn btn-success me-2"
                                onClick={handleBulkAccept}
                                disabled={bulkAccepting}
                            >
                                {bulkAccepting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                        جاري القبول المجمع...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check-double me-1"></i>
                                        قبول مجمع ({selectedOrders.length})
                                    </>
                                )}
                            </button>
                            
                            <button 
                                className="btn btn-danger"
                                onClick={handleBulkDelete}
                            >
                                <i className="fas fa-trash me-1"></i>
                                حذف مجمع ({selectedOrders.length})
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="card mb-4">
                <div className="card-header">
                    <ul className="nav nav-tabs card-header-tabs">
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setActiveFilter('all')}
                            >
                                <i className="fas fa-list me-1"></i>
                                جميع الطلبات ({orders.length})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeFilter === 'pending' ? 'active' : ''}`}
                                onClick={() => setActiveFilter('pending')}
                            >
                                <i className="fas fa-clock me-1"></i>
                                الطلبات المعلقة ({orders.filter(o => o.status === 'Pending').length})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeFilter === 'accepted' ? 'active' : ''}`}
                                onClick={() => setActiveFilter('accepted')}
                            >
                                <i className="fas fa-check me-1"></i>
                                الطلبات المقبولة ({orders.filter(o => o.status === 'Accepted').length})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeFilter === 'delivered' ? 'active' : ''}`}
                                onClick={() => setActiveFilter('delivered')}
                            >
                                <i className="fas fa-truck me-1"></i>
                                الطلبات المسلمة ({orders.filter(o => o.status === 'Delivered').length})
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className={`card text-white ${activeFilter === 'all' ? 'bg-primary' : 'bg-secondary'}`}>
                        <div className="card-body">
                            <h5>إجمالي الطلبات</h5>
                            <h3>{orders.length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className={`card text-white ${activeFilter === 'pending' ? 'bg-warning' : 'bg-secondary'}`}>
                        <div className="card-body">
                            <h5>الطلبات المعلقة</h5>
                            <h3>{orders.filter(o => o.status === 'Pending').length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className={`card text-white ${activeFilter === 'accepted' ? 'bg-success' : 'bg-secondary'}`}>
                        <div className="card-body">
                            <h5>الطلبات المقبولة</h5>
                            <h3>{orders.filter(o => o.status === 'Accepted').length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className={`card text-white ${activeFilter === 'delivered' ? 'bg-info' : 'bg-secondary'}`}>
                        <div className="card-body">
                            <h5>الطلبات المسلمة</h5>
                            <h3>{orders.filter(o => o.status === 'Delivered').length}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Orders Section */}
            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        {activeFilter === 'all' && 'جميع الطلبات'}
                        {activeFilter === 'pending' && 'الطلبات المعلقة - تحتاج قبول الإدارة'}
                        {activeFilter === 'accepted' && 'الطلبات المقبولة'}
                        {activeFilter === 'delivered' && 'الطلبات المسلمة'}
                    </h5>
                    {filteredOrders.length > 0 && activeFilter === 'pending' && (
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="selectAll"
                                checked={selectedOrders.length === filteredOrders.length}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="selectAll">
                                تحديد الكل
                            </label>
                        </div>
                    )}
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            {activeFilter === 'all' && 'لا توجد طلبات'}
                            {activeFilter === 'pending' && 'لا توجد طلبات معلقة! جميع الطلبات تم التعامل معها.'}
                            {activeFilter === 'accepted' && 'لا توجد طلبات مقبولة'}
                            {activeFilter === 'delivered' && 'لا توجد طلبات مسلمة'}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        {activeFilter === 'pending' && <th>اختيار</th>}
                                        <th>رقم الطلب</th>
                                        <th>العميل</th>
                                        <th>رقم الهاتف</th>
                                            <th>البائع</th>
                                        <th>مندوب التوصيل</th>
                                        <th>رقم مندوب التوصيل</th>
                                    
                                        <th>التاريخ</th>
                                        <th>الحالة</th>
                                        <th>طريقة الدفع</th>
                                        <th>الموقع</th>
                                        <th>ملاحظات</th>
                                        <th>المنتجات</th>
                                        <th>الإجمالي</th>
                                        <th>الإجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map(order => {
                                        const originalTotalAmount = readNumberValue(order, 'originalTotalAmount', 'OriginalTotalAmount', 'originalTotal', 'OriginalTotal');
                                        const productDiscount = readNumberValue(order, 'productDiscount', 'ProductDiscount');
                                        const couponDiscount = readNumberValue(order, 'couponDiscount', 'CouponDiscount');
                                        const loyaltyDiscount = readNumberValue(order, 'amountPaidWithPoints', 'AmountPaidWithPoints');
                                        const finalAmountPaid = readNumberValue(order, 'finalAmountPaid', 'FinalAmountPaid', 'grandTotal', 'GrandTotal', 'amountPaidWithMoney', 'AmountPaidWithMoney');
                                        const totalDiscount = productDiscount + couponDiscount + loyaltyDiscount;

                                        return (
                                        <tr key={order.id}>
                                            {activeFilter === 'pending' && (
                                                <td>
                                                    <input 
                                                        type="checkbox" 
                                                        className="form-check-input"
                                                        checked={selectedOrders.includes(order.id)}
                                                        onChange={(e) => handleSelectOrder(order.id, e.target.checked)}
                                                    />
                                                </td>
                                            )}
                                            <td><strong>#{order.trackingNumber}</strong></td>
                                            <td>{order.user ? `${order.user.firstName || order.user.FirstName} ${order.user.lastName || order.user.LastName}` : '—'}</td>
                                            <td>
                                                {order.user && (order.user.phoneNumber || order.user.PhoneNumber) ? (
                                                    <a 
                                                        href={`tel:${order.user.phoneNumber || order.user.PhoneNumber}`}
                                                        className="text-decoration-none"
                                                        title="اتصال بالعميل"
                                                    >
                                                        <i className="fas fa-phone text-success me-1"></i>
                                                        {order.user.phoneNumber || order.user.PhoneNumber}
                                                    </a>
                                                ) : '—'}
                                            </td>
                                            <td>{order.vendor ? order.vendor.name : '—'}</td>
                                            <td>
                                                {order.deliveryDriver ? (
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-motorcycle text-primary me-2"></i>
                                                        <strong>{order.deliveryDriver.name}</strong>
                                                    </div>
                                                ) : (
                                                    <span className="badge bg-secondary">
                                                        <i className="fas fa-clock me-1"></i>
                                                        في انتظار مندوب
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {order.deliveryDriver?.phone ? (
                                                    <a
                                                        href={`tel:${order.deliveryDriver.phone}`}
                                                        className="text-decoration-none"
                                                        title="اتصال بمندوب التوصيل"
                                                    >
                                                        <i className="fas fa-phone text-success me-1"></i>
                                                        {order.deliveryDriver.phone}
                                                    </a>
                                                ) : '—'}
                                            </td>
                                            <td>{formatDateTime(order.createdAt)}</td>
                                            <td>
                                                <span className={`badge ${
                                                    order.status === 'Pending' ? 'bg-warning' :
                                                    order.status === 'Accepted' ? 'bg-success' :
                                                    order.status === 'Delivered' ? 'bg-info' : 'bg-secondary'
                                                }`}>
                                                    {getOrderStatusLabel(order.status)}
                                                </span>
                                            </td>
                                            <td>{getPaymentMethodLabel(order.paymentMethod)}</td>
                                            <td>{order.location}</td>
                                            <td>{order.notes || order.Notes || '—'}</td>
                                            <td>
                                                {order.orderDetails && order.orderDetails.length > 0 ? (
                                                    <button 
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => handleShowProducts(order)}
                                                        title="عرض المنتجات"
                                                    >
                                                        <i className="fas fa-eye me-1"></i>
                                                        {order.orderDetails.length} منتج
                                                    </button>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div className="fw-bold text-success">{formatMoney(finalAmountPaid)}</div>
                                                    {totalDiscount > 0 && (
                                                        <>
                                                            <div className="text-muted text-decoration-line-through">
                                                                {formatMoney(originalTotalAmount + readNumberValue(order, 'deliveryFee', 'DeliveryFee'))}
                                                            </div>
                                                            <div className="text-danger">
                                                                خصومات: {formatMoney(totalDiscount)}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {order.status === 'Pending' && (
                                                        <button 
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => handleAcceptOrder(order.id)}
                                                            disabled={acceptingOrders[order.id]}
                                                        >
                                                            {acceptingOrders[order.id] ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                    جاري القبول...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="fas fa-check me-1"></i>
                                                                    قبول
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                    
                                                    <button 
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteOrder(order.id)}
                                                        disabled={deletingOrders[order.id]}
                                                        title="حذف الطلب"
                                                    >
                                                        {deletingOrders[order.id] ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                            </>
                                                        ) : (
                                                            <i className="fas fa-trash-alt"></i>
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )})}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Products Modal */}
            {showProductsModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-xl"> {/* Made it larger for better view */}
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-shopping-cart me-2"></i>
                                    تفاصيل الطلب #{selectedOrderInfo?.id}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={() => setShowProductsModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {selectedOrderInfo && (
                                    <>
                                        {/* Basic Order Information */}
                                        <div className="row mb-4">
                                            <div className="col-md-4">
                                                <strong>العميل:</strong> {selectedOrderInfo.customerName}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>البائع:</strong> {selectedOrderInfo.vendorName}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>طريقة الدفع:</strong> {getPaymentMethodLabel(selectedOrderInfo.paymentMethod)}
                                            </div>
                                        </div>

                                        {selectedOrderInfo.notes && (
                                            <div className="alert alert-secondary">
                                                <strong>ملاحظات الطلب:</strong> {selectedOrderInfo.notes}
                                            </div>
                                        )}

                                     

                                    </>
                                )}
                                
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>صورة المنتج</th>
                                                <th>المنتج</th>
                                                <th>البائع</th>
                                                <th>الكمية</th>
                                                <th>السعر</th>
                                                <th>اللون/الحجم</th>
                                                <th>المجموع</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrderProducts.map((item, index) => {
                                                const productName = item.Product?.Name || 
                                                                  item.product?.name || 
                                                                  item.ProductName || 
                                                                  item.name ||
                                                                  item.Name ||
                                                                  'منتج غير محدد';
                                                const productImage = getImageUrl(
                                                    item.Product?.ImageUrl || 
                                                    item.product?.imageUrl || 
                                                    item.Product?.imageUrl ||
                                                    item.product?.ImageUrl ||
                                                    item.ImageUrl ||
                                                    item.imageUrl
                                                );
                                                
                                                // Debug product image
                                                console.log(`Product ${productName} debug - FULL ITEM:`, item);
                                                console.log(`Product ${productName} image debug:`, {
                                                    'item.Product': item.Product,
                                                    'item.product': item.product,
                                                    'item.Product?.ImageUrl': item.Product?.ImageUrl,
                                                    'item.product?.imageUrl': item.product?.imageUrl,
                                                    'item.Product?.imageUrl': item.Product?.imageUrl,
                                                    'item.ImageUrl': item.ImageUrl,
                                                    finalImage: productImage,
                                                    hasVendor: item.Product?.HasVendor,
                                                    vendorId: item.Product?.ProductVendorId
                                                });
                                                const vendorName = item.Product?.VendorName || 
                                                                 item.product?.vendorName ||
                                                                 item.Product?.vendorName ||
                                                                 // Fallback to order vendor if product vendor is not available
                                                                 selectedOrderInfo?.vendorName ||
                                                                 'غير محدد';
                                                                 
                                                // Debug logging
                                                console.log('Product vendor debug:', {
                                                    productName: productName,
                                                    vendorName: vendorName,
                                                    hasVendor: item.Product?.HasVendor,
                                                    productVendorId: item.Product?.ProductVendorId,
                                                    vendorEntityId: item.Product?.VendorEntityId,
                                                    orderVendor: selectedOrderInfo?.vendorName
                                                });
                                                const quantity = item.quantity || item.Quantity || 0;
                                                const price = item.price || item.Price || 0;
                                                const total = quantity * price;
                                                
                                                return (
                                                    <tr key={index}>
                                                        <td>
                                                            {productImage ? (
                                                                <img 
                                                                    src={productImage} 
                                                                    alt={productName}
                                                                    style={{
                                                                        width: '60px',
                                                                        height: '60px',
                                                                        objectFit: 'cover',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid #ddd'
                                                                    }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div 
                                                                style={{
                                                                    width: '60px',
                                                                    height: '60px',
                                                                    backgroundColor: '#f8f9fa',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '8px',
                                                                    display: productImage ? 'none' : 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '10px',
                                                                    color: '#6c757d',
                                                                    textAlign: 'center',
                                                                    padding: '2px'
                                                                }}
                                                            >
                                                                {item.product?.isStandalone && item.product?.isImageUrlEmpty ? 
                                                                    'صورة مفقودة' : 'لا توجد صورة'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <strong>{productName}</strong>
                                                            {item.Product?.Description && (
                                                                <div className="text-muted small">
                                                                    {item.Product.Description}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">{vendorName}</span>
                                                        </td>
                                                        <td>
                                                            <span style={quantityBadgeStyle}>{quantity}</span>
                                                        </td>
                                                        <td>{price} ل.س</td>
                                                        <td>
                                                            <div className="small">
                                                                <div>اللون: {item.colorName || item.ColorName || '—'}</div>
                                                                <div>الحجم: {item.sizeName || item.SizeName || '—'}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <strong className="text-success">{total} ل.س</strong>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="table-light">
                                                <td colSpan="5"><strong>الإجمالي قبل الخصومات:</strong></td>
                                                <td><strong>{formatMoney(selectedOrderInfo?.originalTotal)}</strong></td>
                                                <td></td>
                                            </tr>
                                            {selectedOrderInfo?.productDiscount > 0 && (
                                                <tr className="table-warning">
                                                    <td colSpan="5"><strong>خصم المنتجات:</strong></td>
                                                    <td><strong className="text-danger">-{formatMoney(selectedOrderInfo?.productDiscount)}</strong></td>
                                                    <td></td>
                                                </tr>
                                            )}
                                            {selectedOrderInfo?.couponDiscount > 0 && (
                                                <tr className="table-warning">
                                                    <td colSpan="5">
                                                        <strong>خصم الكوبون:</strong>
                                                        {selectedOrderInfo?.couponCode && (
                                                            <span className="badge bg-warning text-dark ms-2">{selectedOrderInfo?.couponCode}</span>
                                                        )}
                                                    </td>
                                                    <td><strong className="text-danger">-{formatMoney(selectedOrderInfo?.couponDiscount)}</strong></td>
                                                    <td></td>
                                                </tr>
                                            )}
                                            <tr className="table-primary">
                                                <td colSpan="5"><strong>الإجمالي بعد خصومات المنتجات والكوبون:</strong></td>
                                                <td><strong>{formatMoney(selectedOrderInfo?.amountAfterDiscount)}</strong></td>
                                                <td></td>
                                            </tr>
                                            <tr className="table-info">
                                                <td colSpan="5"><strong>رسوم التوصيل:</strong></td>
                                                <td><strong className="text-info">{selectedOrderInfo?.deliveryFee > 0 ? formatMoney(selectedOrderInfo?.deliveryFee) : 'مجاني'}</strong></td>
                                                <td></td>
                                            </tr>
                                            {selectedOrderInfo?.amountPaidWithPoints > 0 && (
                                            <tr className="table-danger">
                                                <td colSpan="5"><strong>خصم نقاط الولاء:</strong></td>
                                                <td><strong className="text-danger">-{formatMoney(selectedOrderInfo?.amountPaidWithPoints)}</strong></td>
                                                <td></td>
                                            </tr>
                                            )}
                                            <tr className="table-success">
                                                <td colSpan="6"><strong>المبلغ النهائي (شامل التوصيل بعد الخصم):</strong></td>
                                                <td><strong className="text-success" style={{ fontSize: '1.2rem' }}>
                                                    {formatMoney(selectedOrderInfo?.finalAmountPaid || selectedOrderInfo?.grandTotal)}
                                                </strong></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowProductsModal(false)}
                                >
                                    إغلاق
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminOrdersManagement;
